import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AlertTriangle, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AuthContext } from '@/App';
import { sanitizeHtml } from '../utils/sanitize';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TermsAndConditionsModal = () => {
  const { user, token } = useContext(AuthContext);
  const [activeFestival, setActiveFestival] = useState(null);
  const [showTerms, setShowTerms] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && token) {
      checkTermsStatus();
    }
  }, [user, token]);

  const checkTermsStatus = async () => {
    try {
      // Don't show T&C modal if profile is incomplete (OAuth users)
      if (!user.phone || !user.birthdate || !user.consent_gdpr) {
        setLoading(false);
        return; // Profile must be completed first
      }
      
      // Get active festival
      const festivalResponse = await axios.get(`${API}/festivals/active`);
      const festival = festivalResponse.data;
      setActiveFestival(festival);

      // Check if user is associated with this festival
      const associationResponse = await axios.get(`${API}/festivals/${festival.id}/check-association`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const association = associationResponse.data;
      
      // Show terms modal if user is associated but hasn't accepted terms
      if (association.associated && !association.terms_accepted && festival.terms_conditions) {
        setShowTerms(true);
      }
    } catch (error) {
      console.log('No active festival or user not associated');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptTerms = async () => {
    if (!activeFestival) return;
    
    setAccepting(true);
    try {
      await axios.post(`${API}/users/associate-festival/${activeFestival.id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowTerms(false);
      
      // Show success message
      alert(`Tak! Du er nu tilknyttet ${activeFestival.name}. Du kan nu tilmelde dig vagter.`);
    } catch (error) {
      alert('Fejl ved accept af vilkår: ' + (error.response?.data?.detail || 'Ukendt fejl'));
    } finally {
      setAccepting(false);
    }
  };

  if (loading || !showTerms || !activeFestival) {
    return null;
  }

  return (
    <Dialog open={showTerms} onOpenChange={() => {}}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            <span>Vilkår og Betingelser - {activeFestival.name}</span>
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-blue-900 font-medium">Velkommen til {activeFestival.name}!</p>
                <p className="text-blue-800 text-sm mt-1">
                  For at kunne tilmelde dig vagter skal du først acceptere vores vilkår og betingelser.
                </p>
              </div>
            </div>
          </div>
          
          <div className="border rounded-lg p-4 max-h-96 overflow-y-auto bg-neutral-50">
            <div 
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(activeFestival.terms_conditions) }}
            />
          </div>
          
          <div className="flex items-center justify-end space-x-3">
            <Button
              variant="outline"
              onClick={() => {
                if (confirm('Er du sikker på at du ikke vil acceptere vilkårene? Du kan ikke tilmelde dig vagter uden at acceptere.')) {
                  setShowTerms(false);
                }
              }}
            >
              Afvis
            </Button>
            <Button
              onClick={handleAcceptTerms}
              disabled={accepting}
              className="bg-green-600 hover:bg-green-700"
            >
              {accepting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Accepterer...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Acceptér Vilkår
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TermsAndConditionsModal;